import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import SearchablePromptSelect, { type PromptOption } from '../../../../ui/SearchablePromptSelect';
import DynamicWidgetPreview from '../../widgets/DynamicWidgetPreview';
import NotificationModal from '../../../../ui/overlays/modals/NotificationModal';
import { useNotification } from '../../../../../hooks/useNotification';
import { parseDropdownOptions, parseOptionsString } from '../../../../../utils/dropdownOptions';
import { isDynamicFieldRequired, setDynamicFieldRequired, supportsRequiredFlag } from '../../../../../utils/dynamicFieldRequired';
import type { BaseSectionProps, DynamicWidgetField, DynamicWidgetConfig } from './index';

// Helper to safely get localization strings
const getAdminText = (t: any, key: string, fallback: string): string => {
  return (t.admin as any)?.[key] || fallback;
};

const getCommonText = (t: any, key: string, fallback: string): string => {
  return (t.common as any)?.[key] || fallback;
};

type FieldType = 'dropdown' | 'slider' | 'multiselect' | 'int_field' | 'string_field' | 'image_size' | 'seed' | 'dropdown_prompt' | 'controller_dropdown' | 'hidden_text' | 'hidden_number' | 'hidden_prompt';

interface FieldFormData {
  field_id: string;
  field_type: FieldType;
  label: string;
  tooltip: string;
  config: Record<string, any>;
}

const FIELD_TYPE_OPTIONS: { value: FieldType; label: string }[] = [
  { value: 'string_field', label: 'Text Field' },
  { value: 'int_field', label: 'Number Field' },
  { value: 'slider', label: 'Slider' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'image_size', label: 'Image Size' },
  { value: 'seed', label: 'Seed' },
  { value: 'dropdown_prompt', label: 'Dropdown Prompt' },
  { value: 'controller_dropdown', label: 'Controller Dropdown' },
  { value: 'hidden_text', label: 'Hidden Text' },
  { value: 'hidden_number', label: 'Hidden Number' },
  { value: 'hidden_prompt', label: 'Hidden Prompt' }
];

// `is_required` is deliberately absent (not `false`): an unmarked config stays byte-identical
// until the Required checkbox is used (see utils/dynamicFieldRequired.ts).
const getDefaultConfig = (fieldType: FieldType): Record<string, any> => {
  switch (fieldType) {
    case 'string_field':
      return { min_length: null, max_length: 255, default_value: '', multiline: false, rows: 3 };
    case 'int_field':
      return { min_value: 0, max_value: 100, default_value: 0 };
    case 'slider':
      return { min_value: 0, max_value: 100, step: 1, default_value: 50, design: 'basic' };
    case 'dropdown':
      return { options: [], default_value: '' };
    case 'multiselect':
      return { options: [], max_selections: 5 };
    case 'image_size':
      return { min_width: 64, max_width: 2048, min_height: 64, max_height: 2048, default_width: 768, default_height: 1024, step: 8 };
    case 'seed':
      return { min_value: 0, max_value: 2147483647, default_value: null };
    case 'dropdown_prompt':
      return { options: [], default_value: '' };
    case 'controller_dropdown':
      return { options: [], default_value: '' };
    case 'hidden_text':
      return { default_value: '' };
    case 'hidden_number':
      return { default_value: 0 };
    case 'hidden_prompt':
      return { prompt_id: '', default_value: '' };
    default:
      return {};
  }
};

export default function DynamicWidgetSection({
  formData,
  onFormDataChange,
  language
}: BaseSectionProps) {
  const { getToken } = useAuth();
  const t = useLocalization(language);

  const [showAddField, setShowAddField] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [isWidgetInfoCollapsed, setIsWidgetInfoCollapsed] = useState(false);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const [fieldFormData, setFieldFormData] = useState<FieldFormData>({
    field_id: '',
    field_type: 'string_field',
    label: '',
    tooltip: '',
    config: getDefaultConfig('string_field')
  });

  const [workflowPrompts, setWorkflowPrompts] = useState<PromptOption[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [selectedVariantTab, setSelectedVariantTab] = useState<string>('default');

  useEffect(() => {
    const loadWorkflowPrompts = async () => {
      try {
        setPromptsLoading(true);
        const token = await getToken({ template: 'withmail' });
        const response = await fetch('/api/admin/prompts/workflow', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setWorkflowPrompts(data.prompts || []);
        } else {
          setWorkflowPrompts([]);
        }
      } catch (error) {
        console.error('Error loading workflow prompts:', error);
        setWorkflowPrompts([]);
      } finally {
        setPromptsLoading(false);
      }
    };
    loadWorkflowPrompts();
  }, []);

  const dynamicWidget = formData.dynamic_widget;

  // Get localized field type label
  const getFieldTypeLabel = (fieldType: string): string => {
    if (fieldType === 'controller_dropdown') {
      return getAdminText(t, 'controllerDropdown', 'Controller Dropdown');
    }
    return FIELD_TYPE_OPTIONS.find(o => o.value === fieldType)?.label || fieldType;
  };

  // Initialize widget if needed
  const initializeWidget = useCallback(() => {
    if (!dynamicWidget) {
      onFormDataChange('dynamic_widget', {
        name: '',
        description: '',
        display_order: 0,
        fields: []
      });
    }
  }, [dynamicWidget, onFormDataChange]);

  // Update widget property
  const updateWidgetProperty = (key: string, value: any) => {
    onFormDataChange('dynamic_widget', {
      ...dynamicWidget,
      [key]: value
    });
  };

  // Clear widget
  const clearWidget = () => {
    onFormDataChange('dynamic_widget', null);
  };

  // Export widget to JSON file
  const handleExport = () => {
    if (!dynamicWidget) return;
    const dataStr = JSON.stringify(dynamicWidget, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dynamic_widget_${dynamicWidget.name || 'export'}_${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    showSuccess(getAdminText(t, 'exportSuccess', 'Widget exported successfully'));
  };

  // Import widget from JSON file
  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);

        // Validate structure
        if (typeof data.name !== 'string') {
          throw new Error('Missing or invalid "name" field');
        }
        if (!Array.isArray(data.fields)) {
          throw new Error('Missing or invalid "fields" array');
        }

        // Validate each field
        let controllerCount = 0;
        for (const field of data.fields) {
          if (!field.field_id || typeof field.field_id !== 'string') {
            throw new Error('Each field must have a valid "field_id"');
          }
          if (!field.field_type || typeof field.field_type !== 'string') {
            throw new Error('Each field must have a valid "field_type"');
          }
          if (!field.label || typeof field.label !== 'string') {
            throw new Error('Each field must have a valid "label"');
          }
          if (field.field_type === 'controller_dropdown') {
            controllerCount++;
            // Validate controller options (same checks as saveField)
            const options = field.config_data?.options;
            if (!Array.isArray(options) || options.filter((o: any) => o.value?.trim()).length === 0) {
              throw new Error(getAdminText(t, 'controllerNeedsOptions', 'Controller Dropdown must have at least one option'));
            }
            const optionValues = options.map((o: any) => o.value?.trim()).filter(Boolean);
            if (new Set(optionValues).size !== optionValues.length) {
              throw new Error(getAdminText(t, 'controllerDuplicateValues', 'Controller Dropdown option values must be unique'));
            }
            // Normalize: strip empty-value entries, auto-fill empty labels from values (same as saveField)
            field.config_data.options = options
              .filter((o: any) => o.value?.trim())
              .map((o: any) => ({ ...o, label: o.label?.trim() || o.value.trim() }));
          }
        }
        if (controllerCount > 1) {
          throw new Error(getAdminText(t, 'onlyOneController', 'Only one Controller Dropdown field is allowed per widget'));
        }

        // Apply imported data
        onFormDataChange('dynamic_widget', {
          name: data.name,
          description: data.description || '',
          display_order: data.display_order || 0,
          fields: data.fields
        });
        showSuccess(getAdminText(t, 'importSuccess', 'Widget imported successfully'));
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Invalid JSON file');
      }
    };
    reader.readAsText(file);

    // Reset input value to allow re-importing same file
    event.target.value = '';
  };

  // Reset field form
  const resetFieldForm = () => {
    setFieldFormData({
      field_id: '',
      field_type: 'string_field',
      label: '',
      tooltip: '',
      config: getDefaultConfig('string_field')
    });
    setShowAddField(false);
    setEditingFieldIndex(null);
    setSelectedVariantTab('default');
  };

  // Handle field type change
  const handleFieldTypeChange = (newType: FieldType) => {
    setFieldFormData(prev => ({
      ...prev,
      field_type: newType,
      config: getDefaultConfig(newType)
    }));
    setSelectedVariantTab('default');
  };

  // Add or update field
  const saveField = () => {
    if (!dynamicWidget || !fieldFormData.field_id || !fieldFormData.label) {
      return;
    }

    // Validate: max 1 controller_dropdown per widget
    if (fieldFormData.field_type === 'controller_dropdown') {
      const existingControllerIndex = dynamicWidget.fields.findIndex(
        f => f.field_type === 'controller_dropdown'
      );
      if (existingControllerIndex !== -1 && existingControllerIndex !== editingFieldIndex) {
        showError(getAdminText(t, 'onlyOneController', 'Only one Controller Dropdown is allowed per widget'));
        return;
      }

      // Normalize options: strip empty-value entries, auto-fill empty labels from values
      let normalizedOptions = fieldFormData.config?.options;
      if (Array.isArray(normalizedOptions)) {
        normalizedOptions = normalizedOptions
          .filter((o: any) => o.value?.trim())
          .map((o: any) => ({ ...o, label: o.label?.trim() || o.value.trim() }));
        setFieldFormData(prev => ({
          ...prev,
          config: { ...prev.config, options: normalizedOptions }
        }));
      }

      // Validate: controller_dropdown must have at least one option
      const options = normalizedOptions;
      if (!Array.isArray(options) || options.filter((o: any) => o.value?.trim()).length === 0) {
        showError(getAdminText(t, 'controllerNeedsOptions', 'Controller Dropdown must have at least one option'));
        return;
      }

      // Validate: option values must be unique (duplicates break variant resolution)
      const optionValues = options.map((o: any) => o.value?.trim()).filter(Boolean);
      if (new Set(optionValues).size !== optionValues.length) {
        showError(getAdminText(t, 'controllerDuplicateValues', 'Controller Dropdown option values must be unique'));
        return;
      }
    }

    // Validate: hidden_prompt must have a prompt selected
    if (fieldFormData.field_type === 'hidden_prompt') {
      if (!fieldFormData.config.prompt_id) {
        showError(getAdminText(t, 'hiddenPromptNeedsPrompt', 'Hidden Prompt must have a prompt selected'));
        return;
      }
    }

    const newField: DynamicWidgetField = {
      field_id: fieldFormData.field_id,
      field_type: fieldFormData.field_type,
      label: fieldFormData.label,
      tooltip: fieldFormData.tooltip || null,
      display_order: editingFieldIndex !== null
        ? dynamicWidget.fields[editingFieldIndex].display_order
        : dynamicWidget.fields.length,
      config_data: fieldFormData.config
    };

    let newFields: DynamicWidgetField[];
    if (editingFieldIndex !== null) {
      // Update existing field
      newFields = [...dynamicWidget.fields];
      newFields[editingFieldIndex] = newField;
    } else {
      // Add new field
      newFields = [...dynamicWidget.fields, newField];
    }

    // If editing a field that WAS controller_dropdown but is now a different type,
    // clean up variants from all other fields (same as deleteField cleanup)
    if (editingFieldIndex !== null && fieldFormData.field_type !== 'controller_dropdown') {
      const oldField = dynamicWidget.fields[editingFieldIndex];
      if (oldField?.field_type === 'controller_dropdown') {
        newFields = newFields.map(field => {
          if (field.config_data?.variants) {
            const { variants, ...rest } = field.config_data;
            return { ...field, config_data: rest };
          }
          return field;
        });
      }
    }

    // When saving a controller_dropdown, migrate variant keys in other fields
    // to match renamed/deleted options
    if (fieldFormData.field_type === 'controller_dropdown' && editingFieldIndex !== null) {
      const oldField = dynamicWidget.fields[editingFieldIndex];
      if (oldField?.field_type === 'controller_dropdown' && oldField.config_data?.options) {
        const oldOptions: Array<{ value: string }> = oldField.config_data.options || [];
        const newOptions: Array<{ value: string }> = fieldFormData.config.options || [];

        const newValueSet = new Set(newOptions.map(o => o.value).filter(Boolean));

        // Collect all old option values that no longer exist in the new set.
        // We intentionally do NOT attempt rename detection (inferring that a
        // simultaneous delete+add is a rename) because it is ambiguous: the
        // admin may have deleted option A and added an unrelated option C, and
        // silently copying A's variant overrides onto C would be data corruption.
        // Treating all disappeared values as deletions is safe — the admin can
        // re-configure variant overrides for genuinely renamed options.
        const removedValues = new Set<string>();
        for (const oldOpt of oldOptions) {
          if (oldOpt.value && !newValueSet.has(oldOpt.value)) {
            removedValues.add(oldOpt.value);
          }
        }

        if (removedValues.size > 0) {
          newFields = newFields.map(field => {
            if (field.field_type === 'controller_dropdown' || !field.config_data?.variants) return field;

            const variants = { ...field.config_data.variants };
            let changed = false;

            for (const removed of removedValues) {
              if (removed in variants) {
                delete variants[removed];
                changed = true;
              }
            }

            if (!changed) return field;

            if (Object.keys(variants).length === 0) {
              const { variants: _, ...restConfig } = field.config_data;
              return { ...field, config_data: restConfig };
            }
            return { ...field, config_data: { ...field.config_data, variants } };
          });
        }
      }
    }

    onFormDataChange('dynamic_widget', {
      ...dynamicWidget,
      fields: newFields
    });

    resetFieldForm();
  };

  // Edit field
  const editField = (index: number) => {
    const field = dynamicWidget?.fields[index];
    if (!field) return;

    const configForEdit = { ...field.config_data };
    // Convert string options to array for visual editor
    if ((field.field_type === 'dropdown' || field.field_type === 'multiselect') && typeof configForEdit.options === 'string') {
      configForEdit.options = parseDropdownOptions(configForEdit.options);
    }
    setFieldFormData({
      field_id: field.field_id,
      field_type: field.field_type,
      label: field.label,
      tooltip: field.tooltip || '',
      config: configForEdit
    });
    setEditingFieldIndex(index);
    setSelectedVariantTab('default');
    setShowAddField(true);
  };

  // Delete field
  const deleteField = (index: number) => {
    if (!dynamicWidget) return;

    const deletedField = dynamicWidget.fields[index];
    let newFields = dynamicWidget.fields.filter((_, i) => i !== index);

    // If deleting a controller_dropdown, clean up variants from all other fields
    if (deletedField?.field_type === 'controller_dropdown') {
      newFields = newFields.map(field => {
        if (field.config_data?.variants) {
          const { variants, ...rest } = field.config_data;
          return { ...field, config_data: rest };
        }
        return field;
      });
      setSelectedVariantTab('default');
    }

    // Recalculate display_order
    newFields.forEach((field, i) => {
      field.display_order = i;
    });

    onFormDataChange('dynamic_widget', {
      ...dynamicWidget,
      fields: newFields
    });
  };

  // Move field up/down
  const moveField = (index: number, direction: 'up' | 'down') => {
    if (!dynamicWidget) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= dynamicWidget.fields.length) return;

    const newFields = [...dynamicWidget.fields];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];

    // Update display_order
    newFields.forEach((field, i) => {
      field.display_order = i;
    });

    onFormDataChange('dynamic_widget', {
      ...dynamicWidget,
      fields: newFields
    });
  };

  // Get controller field options (if a controller_dropdown exists in the widget)
  const getControllerOptions = (): Array<{ label: string; value: string }> => {
    if (!dynamicWidget) return [];
    const controllerField = dynamicWidget.fields.find(f => f.field_type === 'controller_dropdown');
    if (!controllerField) return [];
    return Array.isArray(controllerField.config_data?.options) ? controllerField.config_data.options : [];
  };

  // Check if the current field being edited should show variant tabs
  const shouldShowVariantTabs = (): boolean => {
    if (!dynamicWidget) return false;
    if (fieldFormData.field_type === 'controller_dropdown') return false;
    const controllerOptions = getControllerOptions();
    return controllerOptions.length > 0;
  };

  // Update a variant config value
  const updateVariantConfig = (variantKey: string, configKey: string, value: any) => {
    setFieldFormData(prev => {
      const variants = { ...(prev.config.variants || {}) };
      const variant = { ...(variants[variantKey] || {}) };
      if (value === '' || value === null || value === undefined) {
        delete variant[configKey];
      } else {
        variant[configKey] = value;
      }
      // Clean up empty variants
      if (Object.keys(variant).length === 0) {
        delete variants[variantKey];
      } else {
        variants[variantKey] = variant;
      }
      return {
        ...prev,
        config: {
          ...prev.config,
          variants: Object.keys(variants).length > 0 ? variants : undefined
        }
      };
    });
  };

  // Get variant config value (for variant tabs)
  const getVariantValue = (variantKey: string, configKey: string): any => {
    return fieldFormData.config?.variants?.[variantKey]?.[configKey];
  };

  // Render variant tabs UI
  const renderVariantTabs = () => {
    const controllerOptions = getControllerOptions();
    if (controllerOptions.length === 0) return null;

    return (
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-400 mb-2">
          {getAdminText(t, 'variantConfig', 'Variant Configuration')}
        </label>
        <div className="flex flex-wrap gap-1 border-b border-gray-600 pb-1">
          <button
            type="button"
            onClick={() => setSelectedVariantTab('default')}
            className={`px-3 py-1.5 text-xs rounded-t-lg transition-colors ${
              selectedVariantTab === 'default'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
            }`}
          >
            {getAdminText(t, 'defaultVariant', 'Default')}
          </button>
          {controllerOptions.filter(o => o.label && o.value).map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => setSelectedVariantTab(option.value)}
              className={`px-3 py-1.5 text-xs rounded-t-lg transition-colors ${
                selectedVariantTab === option.value
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        {selectedVariantTab !== 'default' && (
          <p className="text-xs text-gray-500 mt-1">
            {getAdminText(t, 'variantOverrideHint', 'Only set values that differ from Default. Empty fields inherit the default config.')}
          </p>
        )}
      </div>
    );
  };

  // Render variant-specific override fields for a given field type
  const renderVariantOverrideFields = (variantKey: string) => {
    const { field_type } = fieldFormData;

    switch (field_type) {
      case 'int_field':
      case 'slider':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.minValue || 'Min Value'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
                </label>
                <input
                  type="number"
                  value={getVariantValue(variantKey, 'min_value') ?? ''}
                  onChange={(e) => updateVariantConfig(variantKey, 'min_value', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder={String(fieldFormData.config.min_value ?? '')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.maxValue || 'Max Value'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
                </label>
                <input
                  type="number"
                  value={getVariantValue(variantKey, 'max_value') ?? ''}
                  onChange={(e) => updateVariantConfig(variantKey, 'max_value', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder={String(fieldFormData.config.max_value ?? '')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                />
              </div>
              {field_type === 'slider' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t.admin?.step || 'Step'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={getVariantValue(variantKey, 'step') ?? ''}
                    onChange={(e) => updateVariantConfig(variantKey, 'step', e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder={String(fieldFormData.config.step ?? '')}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.defaultValue || 'Default Value'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
                </label>
                <input
                  type="number"
                  value={getVariantValue(variantKey, 'default_value') ?? ''}
                  onChange={(e) => updateVariantConfig(variantKey, 'default_value', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder={String(fieldFormData.config.default_value ?? '')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                />
              </div>
            </div>
            {/* Hidden checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!getVariantValue(variantKey, 'hidden')}
                onChange={(e) => updateVariantConfig(variantKey, 'hidden', e.target.checked || null)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
              />
              <span className="text-sm text-gray-300">{getAdminText(t, 'hiddenInVariant', 'Hidden for this variant')}</span>
            </label>
          </div>
        );

      case 'dropdown':
      case 'multiselect': {
        const variantOpts: Array<{ label: string; value: string }> = (() => {
          const raw = getVariantValue(variantKey, 'options');
          if (Array.isArray(raw)) return raw;
          if (typeof raw === 'string' && raw) return parseDropdownOptions(raw);
          return [];
        })();
        const variantCopyId = `variant:${variantKey}`;
        const handleCopyVariant = () => copyOptionsToClipboard(variantOpts, variantCopyId);
        const handlePasteVariant = () =>
          pasteOptionsFromClipboard(variantOpts, merged =>
            updateVariantConfig(variantKey, 'options', merged)
          );
        return (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  {t.admin?.options || 'Options'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
                </label>
                {variantOpts.some(o => o.value) && (
                  <button
                    type="button"
                    onClick={handleCopyVariant}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                      copyStatus === variantCopyId
                        ? 'bg-green-600/30 text-green-300'
                        : 'bg-gray-700/60 hover:bg-gray-600 text-gray-200'
                    }`}
                    title={t.admin?.copyVariables || 'Copy variables'}
                  >
                    <Icon name={ICONS.COPY} size={12} />
                    {copyStatus === variantCopyId
                      ? (t.admin?.variablesCopied || 'Copied!')
                      : (t.admin?.copyVariables || 'Copy variables')}
                  </button>
                )}
              </div>
              {variantOpts.length > 0 ? (
                <div className="space-y-2">
                  {variantOpts.map((option, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={option.label}
                        onChange={(e) => {
                          const newOptions = [...variantOpts];
                          newOptions[idx] = { ...newOptions[idx], label: e.target.value };
                          updateVariantConfig(variantKey, 'options', newOptions);
                        }}
                        placeholder={t.admin?.optionLabel || 'Label'}
                        className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-sm"
                      />
                      <input
                        type="text"
                        value={option.value}
                        onChange={(e) => {
                          const newOptions = [...variantOpts];
                          newOptions[idx] = { ...newOptions[idx], value: e.target.value };
                          updateVariantConfig(variantKey, 'options', newOptions);
                        }}
                        placeholder={t.admin?.optionValue || 'Value'}
                        className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newOptions = variantOpts.filter((_, i) => i !== idx);
                          updateVariantConfig(variantKey, 'options', newOptions.length > 0 ? newOptions : null);
                        }}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        title={t.admin?.removeOption || 'Remove option'}
                      >
                        <Icon name={ICONS.TRASH} size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic py-2">
                  {getAdminText(t, 'variantOptionsHint', 'Using default options. Add options to override.')}
                </p>
              )}
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const newOptions = [...variantOpts, { label: '', value: '' }];
                    updateVariantConfig(variantKey, 'options', newOptions);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg transition-colors"
                >
                  <Icon name={ICONS.PLUS} size={14} />
                  {t.admin?.addOption || 'Add Option'}
                </button>
                <button
                  type="button"
                  onClick={handlePasteVariant}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-700/60 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors"
                  title={t.admin?.pasteVariables || 'Paste variables'}
                >
                  <Icon name={ICONS.COPY} size={14} />
                  {t.admin?.pasteVariables || 'Paste variables'}
                </button>
              </div>
            </div>
            {field_type === 'dropdown' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.defaultValue || 'Default Value'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
                </label>
                <input
                  type="text"
                  value={getVariantValue(variantKey, 'default_value') ?? ''}
                  onChange={(e) => updateVariantConfig(variantKey, 'default_value', e.target.value || null)}
                  placeholder={String(fieldFormData.config.default_value || '')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                />
              </div>
            )}
            {field_type === 'multiselect' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.maxSelections || 'Max Selections'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={getVariantValue(variantKey, 'max_selections') ?? ''}
                  onChange={(e) => updateVariantConfig(variantKey, 'max_selections', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder={String(fieldFormData.config.max_selections ?? '')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                />
              </div>
            )}
            {/* Hidden checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!getVariantValue(variantKey, 'hidden')}
                onChange={(e) => updateVariantConfig(variantKey, 'hidden', e.target.checked || null)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
              />
              <span className="text-sm text-gray-300">{getAdminText(t, 'hiddenInVariant', 'Hidden for this variant')}</span>
            </label>
          </div>
        );
      }

      case 'string_field':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.minLength || 'Min Length'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
                </label>
                <input
                  type="number"
                  value={getVariantValue(variantKey, 'min_length') ?? ''}
                  onChange={(e) => updateVariantConfig(variantKey, 'min_length', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder={String(fieldFormData.config.min_length ?? 'No minimum')}
                  min="0"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.maxLength || 'Max Length'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
                </label>
                <input
                  type="number"
                  value={getVariantValue(variantKey, 'max_length') ?? ''}
                  onChange={(e) => updateVariantConfig(variantKey, 'max_length', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder={String(fieldFormData.config.max_length ?? '255')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.defaultValue || 'Default Value'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
              </label>
              <input
                type="text"
                value={getVariantValue(variantKey, 'default_value') ?? ''}
                onChange={(e) => updateVariantConfig(variantKey, 'default_value', e.target.value || null)}
                placeholder={String(fieldFormData.config.default_value || '')}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
              />
            </div>
            {/* Hidden checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!getVariantValue(variantKey, 'hidden')}
                onChange={(e) => updateVariantConfig(variantKey, 'hidden', e.target.checked || null)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
              />
              <span className="text-sm text-gray-300">{getAdminText(t, 'hiddenInVariant', 'Hidden for this variant')}</span>
            </label>
          </div>
        );

      case 'image_size':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t.admin?.minWidth || 'Min Width'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span></label>
                <input type="number" value={getVariantValue(variantKey, 'min_width') ?? ''} onChange={(e) => updateVariantConfig(variantKey, 'min_width', e.target.value ? parseInt(e.target.value) : null)} placeholder={String(fieldFormData.config.min_width ?? '')} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t.admin?.maxWidth || 'Max Width'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span></label>
                <input type="number" value={getVariantValue(variantKey, 'max_width') ?? ''} onChange={(e) => updateVariantConfig(variantKey, 'max_width', e.target.value ? parseInt(e.target.value) : null)} placeholder={String(fieldFormData.config.max_width ?? '')} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t.admin?.minHeight || 'Min Height'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span></label>
                <input type="number" value={getVariantValue(variantKey, 'min_height') ?? ''} onChange={(e) => updateVariantConfig(variantKey, 'min_height', e.target.value ? parseInt(e.target.value) : null)} placeholder={String(fieldFormData.config.min_height ?? '')} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t.admin?.maxHeight || 'Max Height'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span></label>
                <input type="number" value={getVariantValue(variantKey, 'max_height') ?? ''} onChange={(e) => updateVariantConfig(variantKey, 'max_height', e.target.value ? parseInt(e.target.value) : null)} placeholder={String(fieldFormData.config.max_height ?? '')} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t.admin?.defaultWidth || 'Default Width'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span></label>
                <input type="number" value={getVariantValue(variantKey, 'default_width') ?? ''} onChange={(e) => updateVariantConfig(variantKey, 'default_width', e.target.value ? parseInt(e.target.value) : null)} placeholder={String(fieldFormData.config.default_width ?? '')} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t.admin?.defaultHeight || 'Default Height'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span></label>
                <input type="number" value={getVariantValue(variantKey, 'default_height') ?? ''} onChange={(e) => updateVariantConfig(variantKey, 'default_height', e.target.value ? parseInt(e.target.value) : null)} placeholder={String(fieldFormData.config.default_height ?? '')} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" />
              </div>
            </div>
            {/* Hidden checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!getVariantValue(variantKey, 'hidden')}
                onChange={(e) => updateVariantConfig(variantKey, 'hidden', e.target.checked || null)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
              />
              <span className="text-sm text-gray-300">{getAdminText(t, 'hiddenInVariant', 'Hidden for this variant')}</span>
            </label>
          </div>
        );

      case 'seed':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t.admin?.minValue || 'Min Value'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span></label>
                <input type="number" value={getVariantValue(variantKey, 'min_value') ?? ''} onChange={(e) => updateVariantConfig(variantKey, 'min_value', e.target.value ? parseInt(e.target.value) : null)} placeholder={String(fieldFormData.config.min_value ?? '')} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t.admin?.maxValue || 'Max Value'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span></label>
                <input type="number" value={getVariantValue(variantKey, 'max_value') ?? ''} onChange={(e) => updateVariantConfig(variantKey, 'max_value', e.target.value ? parseInt(e.target.value) : null)} placeholder={String(fieldFormData.config.max_value ?? '')} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" />
              </div>
            </div>
            {/* Hidden checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!getVariantValue(variantKey, 'hidden')}
                onChange={(e) => updateVariantConfig(variantKey, 'hidden', e.target.checked || null)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
              />
              <span className="text-sm text-gray-300">{getAdminText(t, 'hiddenInVariant', 'Hidden for this variant')}</span>
            </label>
          </div>
        );

      case 'hidden_text':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.defaultValue || 'Default Value'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
              </label>
              <input
                type="text"
                value={getVariantValue(variantKey, 'default_value') ?? ''}
                onChange={(e) => updateVariantConfig(variantKey, 'default_value', e.target.value || null)}
                placeholder={String(fieldFormData.config.default_value || '')}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
              />
            </div>
            {/* Hidden checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!getVariantValue(variantKey, 'hidden')}
                onChange={(e) => updateVariantConfig(variantKey, 'hidden', e.target.checked || null)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
              />
              <span className="text-sm text-gray-300">{getAdminText(t, 'hiddenInVariant', 'Hidden for this variant')}</span>
            </label>
          </div>
        );

      case 'hidden_number':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.defaultValue || 'Default Value'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
              </label>
              <input
                type="number"
                value={getVariantValue(variantKey, 'default_value') ?? ''}
                onChange={(e) => updateVariantConfig(variantKey, 'default_value', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder={String(fieldFormData.config.default_value ?? '')}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
              />
            </div>
            {/* Hidden checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!getVariantValue(variantKey, 'hidden')}
                onChange={(e) => updateVariantConfig(variantKey, 'hidden', e.target.checked || null)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
              />
              <span className="text-sm text-gray-300">{getAdminText(t, 'hiddenInVariant', 'Hidden for this variant')}</span>
            </label>
          </div>
        );

      case 'hidden_prompt':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.selectPrompt || 'Select Prompt'} <span className="text-xs text-gray-500">({getAdminText(t, 'override', 'override')})</span>
              </label>
              <SearchablePromptSelect
                value={getVariantValue(variantKey, 'prompt_id') ? String(getVariantValue(variantKey, 'prompt_id')) : null}
                onChange={(promptId) => updateVariantConfig(variantKey, 'prompt_id', promptId || null)}
                prompts={workflowPrompts}
                placeholder={t.admin?.selectPrompt || 'Select prompt...'}
                disabled={promptsLoading}
              />
              {getVariantValue(variantKey, 'prompt_id') && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1">
                    {t.admin?.selectedPromptId || 'Selected Prompt ID'}
                  </label>
                  <div className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-300 font-mono">
                    {getVariantValue(variantKey, 'prompt_id')}
                  </div>
                </div>
              )}
            </div>
            {/* Hidden checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!getVariantValue(variantKey, 'hidden')}
                onChange={(e) => updateVariantConfig(variantKey, 'hidden', e.target.checked || null)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
              />
              <span className="text-sm text-gray-300">{getAdminText(t, 'hiddenInVariant', 'Hidden for this variant')}</span>
            </label>
          </div>
        );

      default:
        // For dropdown_prompt and other types, just show hidden checkbox
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              {getAdminText(t, 'noOverridableFields', 'No overridable fields for this type. You can still hide it per variant.')}
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!getVariantValue(variantKey, 'hidden')}
                onChange={(e) => updateVariantConfig(variantKey, 'hidden', e.target.checked || null)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
              />
              <span className="text-sm text-gray-300">{getAdminText(t, 'hiddenInVariant', 'Hidden for this variant')}</span>
            </label>
          </div>
        );
    }
  };

  // Shared copy/paste helpers for {label, value} option lists.
  // Used by dropdown, multiselect, controller_dropdown (default tab) and
  // dropdown/multiselect variant tabs. Each call site passes its own
  // statusId (for per-button "Copied!" flash) and writer.
  const copyOptionsToClipboard = async (
    opts: Array<{ label: string; value: string }>,
    statusId: string
  ) => {
    const lines = opts
      .filter(o => o.value)
      .map(o => (o.label && o.label !== o.value ? `${o.label}::${o.value}` : o.value));
    if (!lines.length) return;
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopyStatus(statusId);
      window.setTimeout(() => setCopyStatus(prev => (prev === statusId ? null : prev)), 1500);
    } catch {
      showError(t.admin?.copyFailed || 'Copy failed');
    }
  };

  const pasteOptionsFromClipboard = async (
    existing: Array<{ label: string; value: string }>,
    writer: (merged: Array<{ label: string; value: string }>) => void
  ) => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = parseOptionsString(text);
      if (!parsed.length) {
        showError(t.admin?.nothingToPaste || 'Clipboard is empty');
        return;
      }
      const base = existing.filter(o => o.label || o.value);
      writer([...base, ...parsed]);
    } catch {
      showError(t.admin?.pasteFailed || 'Paste failed');
    }
  };

  // Render field configuration form based on type
  const renderFieldConfig = () => {
    const { field_type, config } = fieldFormData;

    switch (field_type) {
      case 'string_field':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.minLength || 'Min Length'}
                </label>
                <input
                  type="number"
                  value={config.min_length ?? ''}
                  onChange={(e) => setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, min_length: e.target.value ? parseInt(e.target.value) : null }
                  }))}
                  placeholder="No minimum"
                  min="0"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.maxLength || 'Max Length'}
                </label>
                <input
                  type="number"
                  value={config.max_length || ''}
                  onChange={(e) => setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, max_length: e.target.value ? parseInt(e.target.value) : '' }
                  }))}
                  placeholder="255"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.multiline || false}
                  onChange={(e) => setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, multiline: e.target.checked }
                  }))}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
                />
                <span className="text-sm text-gray-300">{t.admin?.multiline || 'Multiline'}</span>
              </label>
              {config.multiline && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-300">{t.admin?.rows || 'Rows'}:</label>
                  <input
                    type="number"
                    value={config.rows || 3}
                    onChange={(e) => setFieldFormData(prev => ({
                      ...prev,
                      config: { ...prev.config, rows: parseInt(e.target.value) || 3 }
                    }))}
                    min="2"
                    max="20"
                    className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.defaultValue || 'Default Value'}
              </label>
              {config.multiline ? (
                <textarea
                  value={config.default_value || ''}
                  onChange={(e) => setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, default_value: e.target.value }
                  }))}
                  rows={config.rows || 3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white resize-y"
                />
              ) : (
                <input
                  type="text"
                  value={config.default_value || ''}
                  onChange={(e) => setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, default_value: e.target.value }
                  }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              )}
            </div>
          </div>
        );

      case 'int_field':
      case 'slider':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.minValue || 'Min Value'}
              </label>
              <input
                type="number"
                value={config.min_value ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, min_value: e.target.value ? parseFloat(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.maxValue || 'Max Value'}
              </label>
              <input
                type="number"
                value={config.max_value ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, max_value: e.target.value ? parseFloat(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            {field_type === 'slider' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.step || 'Step'}
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={config.step ?? ''}
                  onChange={(e) => setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, step: e.target.value ? parseFloat(e.target.value) : '' }
                  }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.defaultValue || 'Default Value'}
              </label>
              <input
                type="number"
                value={config.default_value ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, default_value: e.target.value ? parseFloat(e.target.value) : 0 }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            {field_type === 'slider' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {getAdminText(t, 'design', 'Design')}
                </label>
                <select
                  value={config.design || 'basic'}
                  onChange={(e) => setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, design: e.target.value }
                  }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value="basic">{getAdminText(t, 'basic', 'Basic')}</option>
                  <option value="special1">{getAdminText(t, 'special1', 'Special 1')}</option>
                </select>
              </div>
            )}
          </div>
        );

      case 'dropdown':
      case 'multiselect': {
        const ddOptions: Array<{ label: string; value: string }> = Array.isArray(config.options)
          ? config.options
          : parseDropdownOptions(config.options);
        const copyId = 'default';
        const handleCopyVariables = () => copyOptionsToClipboard(ddOptions, copyId);
        const handlePasteVariables = () =>
          pasteOptionsFromClipboard(ddOptions, merged =>
            setFieldFormData(prev => ({
              ...prev,
              config: { ...prev.config, options: merged }
            }))
          );
        return (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  {t.admin?.options || 'Options'} *
                </label>
                {ddOptions.some(o => o.value) && (
                  <button
                    type="button"
                    onClick={handleCopyVariables}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                      copyStatus === copyId
                        ? 'bg-green-600/30 text-green-300'
                        : 'bg-gray-700/60 hover:bg-gray-600 text-gray-200'
                    }`}
                    title={t.admin?.copyVariables || 'Copy variables'}
                  >
                    <Icon name={ICONS.COPY} size={12} />
                    {copyStatus === copyId
                      ? (t.admin?.variablesCopied || 'Copied!')
                      : (t.admin?.copyVariables || 'Copy variables')}
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {ddOptions.map((option, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => {
                        const newOptions = [...ddOptions];
                        newOptions[idx] = { ...newOptions[idx], label: e.target.value };
                        setFieldFormData(prev => ({
                          ...prev,
                          config: { ...prev.config, options: newOptions }
                        }));
                      }}
                      placeholder={t.admin?.optionLabel || 'Label'}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-sm"
                    />
                    <input
                      type="text"
                      value={option.value}
                      onChange={(e) => {
                        const newOptions = [...ddOptions];
                        newOptions[idx] = { ...newOptions[idx], value: e.target.value };
                        setFieldFormData(prev => ({
                          ...prev,
                          config: {
                            ...prev.config,
                            options: newOptions,
                            default_value: prev.config.default_value === option.value ? e.target.value : prev.config.default_value
                          }
                        }));
                      }}
                      placeholder={t.admin?.optionValue || 'Value'}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newOptions = ddOptions.filter((_, i) => i !== idx);
                        setFieldFormData(prev => ({
                          ...prev,
                          config: {
                            ...prev.config,
                            options: newOptions,
                            default_value: prev.config.default_value === option.value ? '' : prev.config.default_value
                          }
                        }));
                      }}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                      title={t.admin?.removeOption || 'Remove option'}
                    >
                      <Icon name={ICONS.TRASH} size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const newOptions = [...ddOptions, { label: '', value: '' }];
                    setFieldFormData(prev => ({
                      ...prev,
                      config: { ...prev.config, options: newOptions }
                    }));
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg transition-colors"
                >
                  <Icon name={ICONS.PLUS} size={14} />
                  {t.admin?.addOption || 'Add Option'}
                </button>
                <button
                  type="button"
                  onClick={handlePasteVariables}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-700/60 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors"
                  title={t.admin?.pasteVariables || 'Paste variables'}
                >
                  <Icon name={ICONS.COPY} size={14} />
                  {t.admin?.pasteVariables || 'Paste variables'}
                </button>
              </div>
            </div>
            {field_type === 'dropdown' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.defaultValue || 'Default Value'}
                </label>
                <select
                  value={config.default_value || ''}
                  onChange={(e) => setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, default_value: e.target.value || undefined }
                  }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value="">{getAdminText(t, 'noDefault', 'No default')}</option>
                  {ddOptions.filter(o => o.value).map((option, idx) => (
                    <option key={idx} value={option.value}>
                      {option.label || option.value}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {field_type === 'multiselect' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.maxSelections || 'Max Selections'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.max_selections ?? ''}
                  onChange={(e) => setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, max_selections: e.target.value ? parseInt(e.target.value) : '' }
                  }))}
                  placeholder="No limit"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
            )}
          </div>
        );
      }

      case 'image_size':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Min Width</label>
              <input
                type="number"
                value={config.min_width ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, min_width: e.target.value ? parseInt(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Max Width</label>
              <input
                type="number"
                value={config.max_width ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, max_width: e.target.value ? parseInt(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Min Height</label>
              <input
                type="number"
                value={config.min_height ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, min_height: e.target.value ? parseInt(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Max Height</label>
              <input
                type="number"
                value={config.max_height ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, max_height: e.target.value ? parseInt(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Default Width</label>
              <input
                type="number"
                value={config.default_width ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, default_width: e.target.value ? parseInt(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Default Height</label>
              <input
                type="number"
                value={config.default_height ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, default_height: e.target.value ? parseInt(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Step</label>
              <input
                type="number"
                value={config.step ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, step: e.target.value ? parseInt(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
          </div>
        );

      case 'seed':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Min Value</label>
              <input
                type="number"
                value={config.min_value ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, min_value: e.target.value ? parseInt(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Max Value</label>
              <input
                type="number"
                value={config.max_value ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, max_value: e.target.value ? parseInt(e.target.value) : '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
          </div>
        );

      case 'dropdown_prompt': {
        const options: Array<{ label: string; prompt_id: string }> = Array.isArray(config.options) ? config.options : [];
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.options || 'Options'}
              </label>
              <div className="space-y-2">
                {options.map((option, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => {
                        const newOptions = [...options];
                        newOptions[idx] = { ...newOptions[idx], label: e.target.value };
                        setFieldFormData(prev => ({
                          ...prev,
                          config: { ...prev.config, options: newOptions }
                        }));
                      }}
                      placeholder={t.admin?.optionLabel || 'Option label'}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-sm"
                    />
                    <SearchablePromptSelect
                      value={option.prompt_id ? String(option.prompt_id) : null}
                      onChange={(promptId) => {
                        const newOptions = [...options];
                        newOptions[idx] = { ...newOptions[idx], prompt_id: promptId || '' };
                        setFieldFormData(prev => ({
                          ...prev,
                          config: { ...prev.config, options: newOptions }
                        }));
                      }}
                      prompts={workflowPrompts}
                      placeholder={t.admin?.selectPrompt || 'Select prompt...'}
                      disabled={promptsLoading}
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newOptions = options.filter((_, i) => i !== idx);
                        setFieldFormData(prev => ({
                          ...prev,
                          config: {
                            ...prev.config,
                            options: newOptions,
                            default_value: String(prev.config.default_value || '') === String(option.prompt_id || '') ? '' : prev.config.default_value
                          }
                        }));
                      }}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                      title={t.admin?.removeOption || 'Remove option'}
                    >
                      <Icon name={ICONS.TRASH} size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  const newOptions = [...options, { label: '', prompt_id: '' }];
                  setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, options: newOptions }
                  }));
                }}
                className="mt-2 flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg transition-colors"
              >
                <Icon name={ICONS.PLUS} size={14} />
                {t.admin?.addOption || 'Add Option'}
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.defaultValue || 'Default Value'}
              </label>
              <select
                value={config.default_value || ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, default_value: e.target.value || '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="">{getAdminText(t, 'noDefault', 'No default')}</option>
                {options.filter(o => o.label && o.prompt_id).map((option, idx) => (
                  <option key={idx} value={String(option.prompt_id)}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }

      case 'controller_dropdown': {
        const ctrlOptions: Array<{ label: string; value: string }> = Array.isArray(config.options) ? config.options : [];
        const ctrlCopyId = 'controller';
        const handleCopyCtrl = () => copyOptionsToClipboard(ctrlOptions, ctrlCopyId);
        const handlePasteCtrl = () =>
          pasteOptionsFromClipboard(ctrlOptions, merged => {
            // Controller values must match [a-zA-Z0-9_-] — sanitize pasted values
            // so they can be used as variant keys downstream.
            const sanitized = merged.map(o => ({
              label: o.label,
              value: (o.value || '').replace(/[^a-zA-Z0-9_-]/g, '')
            }));
            setFieldFormData(prev => ({
              ...prev,
              config: { ...prev.config, options: sanitized }
            }));
          });
        return (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-300">
                  {t.admin?.options || 'Options'} *
                </label>
                {ctrlOptions.some(o => o.value) && (
                  <button
                    type="button"
                    onClick={handleCopyCtrl}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                      copyStatus === ctrlCopyId
                        ? 'bg-green-600/30 text-green-300'
                        : 'bg-gray-700/60 hover:bg-gray-600 text-gray-200'
                    }`}
                    title={t.admin?.copyVariables || 'Copy variables'}
                  >
                    <Icon name={ICONS.COPY} size={12} />
                    {copyStatus === ctrlCopyId
                      ? (t.admin?.variablesCopied || 'Copied!')
                      : (t.admin?.copyVariables || 'Copy variables')}
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {ctrlOptions.map((option, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => {
                        const newOptions = [...ctrlOptions];
                        newOptions[idx] = { ...newOptions[idx], label: e.target.value };
                        setFieldFormData(prev => ({
                          ...prev,
                          config: { ...prev.config, options: newOptions }
                        }));
                      }}
                      placeholder={t.admin?.optionLabel || 'Option label'}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-sm"
                    />
                    <input
                      type="text"
                      value={option.value}
                      onChange={(e) => {
                        const newValue = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
                        const oldValue = option.value;
                        const newOptions = [...ctrlOptions];
                        newOptions[idx] = { ...newOptions[idx], value: newValue };
                        setFieldFormData(prev => ({
                          ...prev,
                          config: {
                            ...prev.config,
                            options: newOptions,
                            // Update default_value if it matched the old option value.
                            // Guard with oldValue check: for new empty options (oldValue === ''),
                            // we must not accidentally promote the typed value to default.
                            default_value: (oldValue && prev.config.default_value === oldValue) ? newValue : prev.config.default_value
                          }
                        }));
                      }}
                      placeholder={t.admin?.optionValue || 'Option value'}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newOptions = ctrlOptions.filter((_, i) => i !== idx);
                        setFieldFormData(prev => ({
                          ...prev,
                          config: {
                            ...prev.config,
                            options: newOptions,
                            default_value: prev.config.default_value === option.value ? '' : prev.config.default_value
                          }
                        }));
                      }}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                      title={t.admin?.removeOption || 'Remove option'}
                    >
                      <Icon name={ICONS.TRASH} size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const newOptions = [...ctrlOptions, { label: '', value: '' }];
                    setFieldFormData(prev => ({
                      ...prev,
                      config: { ...prev.config, options: newOptions }
                    }));
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg transition-colors"
                >
                  <Icon name={ICONS.PLUS} size={14} />
                  {t.admin?.addOption || 'Add Option'}
                </button>
                <button
                  type="button"
                  onClick={handlePasteCtrl}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-700/60 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors"
                  title={t.admin?.pasteVariables || 'Paste variables'}
                >
                  <Icon name={ICONS.COPY} size={14} />
                  {t.admin?.pasteVariables || 'Paste variables'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {getAdminText(t, 'controllerOptionsHelp', 'Each option needs a display label and a unique value identifier. The value is used to match variant configs in dependent fields.')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.defaultValue || 'Default Value'}
              </label>
              <select
                value={config.default_value || ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, default_value: e.target.value || '' }
                }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="">{getAdminText(t, 'noDefault', 'No default')}</option>
                {ctrlOptions.filter(o => o.label && o.value).map((option, idx) => (
                  <option key={idx} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }

      case 'hidden_text':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg">
              <Icon name={ICONS.INFO} size={16} className="text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-400">{t.admin?.hiddenFieldHint || 'Hidden — not visible to users'}</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.defaultValue || 'Default Value'}
              </label>
              <input
                type="text"
                value={config.default_value || ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, default_value: e.target.value }
                }))}
                placeholder={t.admin?.hiddenTextPlaceholder || 'Constant text value...'}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
              />
            </div>
          </div>
        );

      case 'hidden_number':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg">
              <Icon name={ICONS.INFO} size={16} className="text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-400">{t.admin?.hiddenFieldHint || 'Hidden — not visible to users'}</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.defaultValue || 'Default Value'}
              </label>
              <input
                type="number"
                value={config.default_value ?? ''}
                onChange={(e) => setFieldFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, default_value: e.target.value ? parseFloat(e.target.value) : 0 }
                }))}
                placeholder="0"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
              />
            </div>
          </div>
        );

      case 'hidden_prompt':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg">
              <Icon name={ICONS.INFO} size={16} className="text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-400">{t.admin?.hiddenFieldHint || 'Hidden — not visible to users'}</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.admin?.selectPrompt || 'Select Prompt'}
              </label>
              <SearchablePromptSelect
                value={config.prompt_id ? String(config.prompt_id) : null}
                onChange={(promptId) => {
                  setFieldFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, prompt_id: promptId || '' }
                  }));
                }}
                prompts={workflowPrompts}
                placeholder={t.admin?.selectPrompt || 'Select prompt...'}
                disabled={promptsLoading}
              />
              {config.prompt_id && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1">
                    {t.admin?.selectedPromptId || 'Selected Prompt ID'}
                  </label>
                  <div className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-300 font-mono">
                    {config.prompt_id}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Convert fields for preview (config_data needs to be JSON string)
  const previewFields = (dynamicWidget?.fields || []).map(field => ({
    id: field.display_order,
    field_type: field.field_type,
    field_id: field.field_id,
    label: field.label,
    tooltip: field.tooltip || undefined,
    display_order: field.display_order,
    config_data: JSON.stringify(field.config_data)
  }));

  return (
    <div className="space-y-6">
      {/* Notification */}
      <NotificationModal
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={hideNotification}
      />

      {/* Widget Configuration */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Icon name={ICONS.SLIDERS} size={20} className="text-purple-400" />
            {t.admin?.dynamicWidget || 'Dynamic Widget'}
          </h3>
          <div className="flex items-center gap-2">
            {/* Import button - always visible */}
            <label className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-colors cursor-pointer">
              <Icon name={ICONS.UPLOAD} size={16} />
              {getAdminText(t, 'import', 'Import')}
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />
            </label>

            {dynamicWidget && (
              <>
                {/* Export button */}
                <button
                  type="button"
                  onClick={handleExport}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg transition-colors"
                >
                  <Icon name={ICONS.DOWNLOAD} size={16} />
                  {getAdminText(t, 'export', 'Export')}
                </button>

                {/* Remove button */}
                <button
                  type="button"
                  onClick={clearWidget}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors"
                >
                  <Icon name={ICONS.TRASH} size={16} />
                  {getAdminText(t, 'removeWidget', 'Remove Widget')}
                </button>
              </>
            )}

            {!dynamicWidget && (
              <button
                type="button"
                onClick={initializeWidget}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                <Icon name={ICONS.PLUS} size={16} />
                {t.admin?.createWidget || 'Create Widget'}
              </button>
            )}
          </div>
        </div>

        <p className="text-sm text-gray-400 mb-6">
          {getAdminText(t, 'dynamicWidgetDesc', 'Create a custom widget with configurable fields embedded directly in this workflow.')}
        </p>

        {dynamicWidget && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Configuration Panel */}
            <div className="space-y-6">
              {/* Basic Info - Collapsible */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setIsWidgetInfoCollapsed(!isWidgetInfoCollapsed)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-700/30 transition-colors"
                >
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <Icon name={ICONS.INFO} size={16} className="text-blue-400" />
                    {getAdminText(t, 'widgetInfo', 'Widget Info')}
                  </h4>
                  <Icon
                    name={ICONS.CHEVRON_DOWN}
                    size={16}
                    className={`text-gray-400 transition-transform duration-200 ${isWidgetInfoCollapsed ? '-rotate-90' : ''}`}
                  />
                </button>

                <div className={`transition-all duration-200 ${isWidgetInfoCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[500px] opacity-100'}`}>
                  <div className="px-4 pb-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">
                        {t.admin?.widgetName || 'Widget Name'}
                      </label>
                      <input
                        type="text"
                        value={dynamicWidget.name}
                        onChange={(e) => updateWidgetProperty('name', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                        placeholder="e.g., Quality Settings"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">
                        {t.admin?.widgetDescription || 'Description'}
                      </label>
                      <input
                        type="text"
                        value={dynamicWidget.description || ''}
                        onChange={(e) => updateWidgetProperty('description', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                        placeholder="Brief description"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">
                        {t.admin?.displayOrder || 'Display Order'}
                      </label>
                      <input
                        type="number"
                        value={dynamicWidget.display_order}
                        onChange={(e) => updateWidgetProperty('display_order', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                        min="0"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {getAdminText(t, 'displayOrderDesc', 'Position among other widgets (lower = first)')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fields List */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <Icon name={ICONS.GRID} size={16} className="text-green-400" />
                    {t.admin?.fields || 'Fields'} ({dynamicWidget.fields.length})
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      resetFieldForm();
                      setShowAddField(true);
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg transition-colors"
                  >
                    <Icon name={ICONS.PLUS} size={14} />
                    {t.admin?.addField || 'Add Field'}
                  </button>
                </div>

                {dynamicWidget.fields.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Icon name={ICONS.SLIDERS} size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{getAdminText(t, 'noFields', 'No fields added yet')}</p>
                    <p className="text-xs mt-1">{getAdminText(t, 'addFieldHint', 'Click "Add Field" to get started')}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dynamicWidget.fields
                      .sort((a, b) => a.display_order - b.display_order)
                      .map((field, index) => (
                        <div
                          key={field.field_id}
                          className="flex items-center gap-2 p-3 bg-gray-700/50 border border-gray-600 rounded-lg group"
                        >
                          {/* Drag handle / order buttons */}
                          <div className="flex flex-col gap-0.5">
                            <button
                              type="button"
                              onClick={() => moveField(index, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Icon name={ICONS.CHEVRON_UP} size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveField(index, 'down')}
                              disabled={index === dynamicWidget.fields.length - 1}
                              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Icon name={ICONS.CHEVRON_DOWN} size={12} />
                            </button>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium truncate">{field.label}</span>
                              <span className="text-xs text-gray-500">({field.field_id})</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {getFieldTypeLabel(field.field_type)}
                            </div>
                          </div>

                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => editField(index)}
                              className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                              title={t.common?.edit || 'Edit'}
                            >
                              <Icon name={ICONS.EDIT} size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteField(index)}
                              className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                              title={t.common?.delete || 'Delete'}
                            >
                              <Icon name={ICONS.TRASH} size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            {/* Preview Panel */}
            <div className="lg:sticky lg:top-4">
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                  <Icon name={ICONS.EYE} size={16} className="text-yellow-400" />
                  {getAdminText(t, 'preview', 'Preview')}
                </h4>

                <div className="w-80 mx-auto">
                  <DynamicWidgetPreview
                    widgetName={dynamicWidget.name || 'Untitled Widget'}
                    widgetDescription={dynamicWidget.description || undefined}
                    fields={previewFields}
                    selectedLanguage={language}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Add/Edit Field Modal */}
      {showAddField && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">
                {editingFieldIndex !== null
                  ? (t.admin?.editField || 'Edit Field')
                  : (t.admin?.addField || 'Add Field')}
              </h3>
              <button
                type="button"
                onClick={resetFieldForm}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Icon name={ICONS.X} size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Field ID */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.fieldId || 'Field ID'} *
                </label>
                <input
                  type="text"
                  value={fieldFormData.field_id}
                  onChange={(e) => setFieldFormData(prev => ({
                    ...prev,
                    field_id: e.target.value.replace(/[^a-zA-Z0-9_]/g, '')
                  }))}
                  placeholder="e.g., quality_level"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {getAdminText(t, 'fieldIdHelp', 'Unique identifier (letters, numbers, underscores only)')}
                </p>
              </div>

              {/* Field Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.fieldType || 'Field Type'} *
                </label>
                <select
                  value={fieldFormData.field_type}
                  onChange={(e) => handleFieldTypeChange(e.target.value as FieldType)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  {FIELD_TYPE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {getFieldTypeLabel(option.value)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {getAdminText(t, 'label', 'Label')} *
                </label>
                <input
                  type="text"
                  value={fieldFormData.label}
                  onChange={(e) => setFieldFormData(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="e.g., Quality Level"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>

              {/* Tooltip */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.admin?.tooltip || 'Tooltip'}
                </label>
                <input
                  type="text"
                  value={fieldFormData.tooltip}
                  onChange={(e) => setFieldFormData(prev => ({ ...prev, tooltip: e.target.value }))}
                  placeholder="Help text for users"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>

              {/* Type-specific config */}
              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">
                  {getAdminText(t, 'fieldConfig', 'Field Configuration')}
                </h4>
                {shouldShowVariantTabs() && renderVariantTabs()}
                {/* Requiredness is base-only: variants never change it */}
                {supportsRequiredFlag(fieldFormData.field_type) &&
                  !(shouldShowVariantTabs() && selectedVariantTab !== 'default') && (
                  <label className="flex items-start gap-2 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isDynamicFieldRequired(fieldFormData.config)}
                      onChange={(e) => {
                        const required = e.target.checked;
                        setFieldFormData(prev => ({ ...prev, config: setDynamicFieldRequired(prev.config, required) }));
                      }}
                      className="mt-0.5 w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/20"
                    />
                    <span>
                      <span className="block text-sm text-gray-300">{getAdminText(t, 'requiredField', 'Required field')}</span>
                      <span className="block text-xs text-gray-500">{getAdminText(t, 'requiredFieldHint', 'Generation is blocked until this field has a value. Applies to every variant; MCP profiles publish it as a required argument.')}</span>
                    </span>
                  </label>
                )}
                {shouldShowVariantTabs() && selectedVariantTab !== 'default'
                  ? renderVariantOverrideFields(selectedVariantTab)
                  : renderFieldConfig()
                }
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-700">
              <button
                type="button"
                onClick={resetFieldForm}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                {t.common?.cancel || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={saveField}
                disabled={!fieldFormData.field_id || !fieldFormData.label}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {editingFieldIndex !== null
                  ? (t.common?.save || 'Save')
                  : (t.admin?.addField || 'Add Field')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
